<template>
  <div :class="$style.appContainer">
    <NuxtLayout v-cloak>
      <NuxtRouteAnnouncer />
      <NuxtPage />
    </NuxtLayout>
    <Toast />
  </div>
</template>
<script setup lang="ts">
  import { useSessionRefresh } from '~/composables/useSessionRefresh'

  const route = useRoute()

  const defaultPageTitle = computed(() => {
    const path = route.path
    if (path === '/') return 'Yogen - AI-Powered Hiring Process Platform'
    if (path.startsWith('/login')) return 'Log In | Yogen'
    if (path.startsWith('/signup')) return 'Sign Up | Yogen'
    if (path.startsWith('/forgotPassword')) return 'Reset Password | Yogen'
    if (path.startsWith('/home')) return 'Dashboard | Yogen'
    if (path.startsWith('/jobs/kit')) return 'Hiring Kit | Yogen'
    if (path.startsWith('/jobs')) return 'Positions | Yogen'
    if (path.startsWith('/audit')) return 'Audit Reports | Yogen'
    if (path.startsWith('/training')) return 'Training | Yogen'
    if (path.startsWith('/organizationSettings')) return 'Settings | Yogen'
    if (path.startsWith('/help')) return 'Help & Resources | Yogen'
    if (path.startsWith('/process')) return 'Hiring Process | Yogen'
    return 'Yogen'
  })

  useHead({ title: defaultPageTitle })

  useSeoMeta({
    ogTitle: 'Yogen',
    description: 'Yogen! Creating great hiring process to hiring great people!',
    ogDescription:
      'Yogen! Creating great hiring process to hiring great people!',
    // ogImage: 'https://example.com/image.png',
    ogType: 'website',
  })
  // const baseURL = import.meta.client ? useRuntimeConfig().public.apiBaseUrl : ''
  // console.log('>>>baseURL', baseURL)
  useSessionRefresh()

  const scrollToTop = async () => {
    if (!import.meta.client) {
      return
    }

    await nextTick()
    window.scrollTo({ left: 0, top: 0, behavior: 'auto' })

    const mainContent = document.getElementById('main-content')
    if (mainContent) {
      mainContent.scrollTop = 0
    }
  }

  watch(
    () => [route.path, JSON.stringify(route.query)] as const,
    async ([newPath, newQuery], [oldPath, oldQuery]) => {
      if (newPath === oldPath && newQuery === oldQuery) return

      // Hiring Kit tabs live in the query string. Changing only that query must
      // not eject the user back to the top of this long, role-specific page.
      const isHiringKitTabChange =
        newPath === oldPath && /^\/jobs\/kit\/[^/]+$/.test(newPath)
      if (isHiringKitTabChange) return

      await scrollToTop()
    },
  )
</script>
<style module>
  .appContainer {
    min-height: 100dvh;
    min-height: 100vh; /* fallback for older browsers */
    overflow: visible;
  }
</style>
<style>
  [v-cloak] {
    display: none;
  }
  .page-enter-active,
  .page-leave-active {
    transition: all 0.2s ease-in-out;
  }
  .page-enter-from,
  .page-leave-to {
    opacity: 0.2;
    filter: blur(0.5rem);
  }

  .quickFade-enter-active,
  .quickFade-leave-active {
    transition: opacity 0.3s ease;
  }

  .quickFade-enter-from,
  .quickFade-leave-to {
    opacity: 0;
  }

  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.5s ease-in-out;
  }

  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
  }

  .icon-rotate-leave-active,
  .icon-rotate-enter-active {
    transition:
      opacity 0.1s ease,
      transform 0.1s ease;
  }
  .icon-rotate-enter-from {
    transform: rotate(-45deg);
  }

  /* .icon-rotate-leave-to {
  } */

  .fade-selection-enter-active,
  .fade-selection-leave-active {
    transition: opacity 0.3s ease-in-out;
  }

  .fade-selection-leave-to,
  .fade-selection-enter-from {
    opacity: 0.8;
  }

  /* .fade-selection-leave-to  */

  @media (prefers-reduced-motion: reduce) {
    /* Target all transition classes */
    [class*='-enter-active'],
    [class*='-leave-active'] {
      transition: none !important;
    }

    /* For animations */
    /* [class*="-transition"] * {
      animation-duration: 0.001s !important;
      animation-delay: 0s !important;
    } */
  }
</style>
